import { cls } from "app/utils";
import { getPaymentIconClasses } from "app/utils/paymentUtils";
import { PaymentMethodIcon } from "app/routes/Calendar/PaymentIcons";
import React from "react";
import { DateTime } from "luxon";

import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { formatTime } from "app/utils/luxonFormats";
import { eventTint } from "app/utils/calendarColors";
import styles from "./styles.module.scss";
import { titleWithLabel } from "./titleWithLabel";
import {
  resourceCardLines,
  resourceCardTitle,
  resourceLineShown,
} from "./cardLabels";

type PropsDef = {
  event: any;
  timeText: string;
  timeZone?: string;
  // The bay of the session, on the time line. The Coaches view only.
  resourceLabel?: string;
  // The coach of the session, on a line of its own. The Resources view only.
  coachName?: string;
  // True while the facility calendar draws the bay columns.
  facilityResourceView?: boolean;
};

export default function LessonSchedule(props: PropsDef) {
  const {
    durationInMinutes,
    eventColor,
    payment_method: paymentMethod,
    payment_status: paymentStatus,
    online_payment_type: onlinePaymentType,
    expires_at: expiresAt,
    lessonRateTitle,
    studentProfiles,
  } = props.event.extendedProps;

  const { preferences } = useCalendarPreferences();
  const startTimeString = formatTime(
    DateTime.fromJSDate(props.event.start, { zone: props.timeZone }),
    preferences.timeFormat,
  );

  const leftBarStyle = eventColor ? { backgroundColor: eventColor } : {};
  // Calendar colour coding: a session whose booking option has a colour is
  // shaded in it; anything else keeps the white card.
  const tint = eventTint(eventColor);

  // The Resources view names the booking option on the title line (R10) and
  // the athletes on a line of their own, joined with ", " (R11). The height
  // of the card sets which lines show (see `resourceCardLines`). The Coaches
  // view keeps the athlete name (or the count) as the title, unchanged.
  const isResourceCard = props.facilityResourceView === true;
  const lines = resourceCardLines(props.coachName, durationInMinutes);
  const cardTitle =
    isResourceCard && lessonRateTitle ? lessonRateTitle : props.event.title;
  const titleText = isResourceCard
    ? resourceCardTitle(cardTitle, props.coachName, lines)
    : titleWithLabel(cardTitle, props.resourceLabel, durationInMinutes);
  const athleteNames = isResourceCard
    ? (studentProfiles ?? [])
        .map((studentProfile: any) => studentProfile.fullName)
        .filter(Boolean)
        .join(", ")
    : "";
  const showAthleteLine =
    isResourceCard && Boolean(athleteNames) && lines.athleteLine;
  const showCoachLine = isResourceCard && lines.coachLine;
  const showTimeLine = !isResourceCard || lines.timeLine;

  return (
    <div
      className={cls(styles.event_container, {
        [styles.small_event]: durationInMinutes < 30,
      })}
      style={tint ? { backgroundColor: tint.fill } : undefined}
      data-testid="calendar-event-session"
    >
      <div className={styles.lesson_schedule_bar} style={leftBarStyle} />
      <div
        className={cls(styles.lesson_schedule_event, {
          [styles.lesson_schedule_event_resource]: isResourceCard,
        })}
      >
        <div
          className={cls(styles.event_title, {
            [styles.event_title_wrap]: isResourceCard && lines.wrapTitle,
          })}
          style={tint ? { color: tint.ink } : undefined}
        >
          {titleText}
        </div>
        {showAthleteLine && (
          <div className={styles.event_athletes} style={tint ? { color: tint.muted } : undefined}>{athleteNames}</div>
        )}
        {showCoachLine && (
          <div className={styles.event_coach} style={tint ? { color: tint.muted } : undefined}>{props.coachName}</div>
        )}
        {showTimeLine && (
          <div className={styles.event_info_container}>
            <div className={styles.event_time} style={tint ? { color: tint.muted } : undefined}>
              {resourceLineShown(props.resourceLabel, durationInMinutes)
                ? `${startTimeString} \u00B7 ${props.resourceLabel}`
                : startTimeString}
            </div>
            <div
              className={cls(styles.payment_container, {
                [styles.payment_container_larger]: durationInMinutes > 60,
              })}
            >
              {paymentMethod === "online" || paymentMethod === "offline" ? (
                <PaymentMethodIcon
                  paymentMethod={paymentMethod}
                  onlinePaymentType={onlinePaymentType}
                  className={cls(styles.payment_icon, {
                    [styles.pending]:
                      paymentStatus === "pending" || !!expiresAt,
                    [styles.failed]:
                      paymentStatus === "past_due" ||
                      paymentStatus === "failed",
                  })}
                />
              ) : (
                <i
                  className={cls(
                    ...getPaymentIconClasses(paymentMethod, styles),
                    styles.payment_icon,
                    {
                      [styles.pending]:
                        paymentStatus === "pending" || !!expiresAt,
                      [styles.failed]:
                        paymentStatus === "past_due" ||
                        paymentStatus === "failed",
                    },
                  )}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
